import React from 'react'
import clsx from 'clsx'
import { getMarginClasses, getPaddingClasses } from '@sainsburys-tech/style'

import type { ContainerProps } from './Container.types'
import { packageDetails } from '../ops/utils/packageDetails'
import { polymorphicForwardRef } from './global-utils'
import './css/main.css'

const overflowXAuto = {
  xs: 'xs:ds-overflow-x-auto',
  sm: 'sm:ds-overflow-x-auto',
  md: 'md:ds-overflow-x-auto',
  lg: 'lg:ds-overflow-x-auto'
}

const defaultElement = 'span'

export type ContainerContextType = {
  overflow?: boolean | 'xs' | 'sm' | 'md' | 'lg'
}

export const ContainerContext = React.createContext<
  ContainerContextType | undefined
>(undefined)

/**
 * The `Container` component is a wrapper for the `GridWrapper` component.
 * It provides a max-width and horizontal padding to the grid.
 * @component
 */
export const Container = polymorphicForwardRef<
  typeof defaultElement,
  ContainerProps
>(
  (
    {
      as,
      children,
      className,
      fluid,
      margin,
      size,
      padding,
      overflow,
      testid,
      ...rest
    },
    ref?
  ) => {
    const Component = as || 'div'
    return (
      <Component
        className={clsx(
          'ds-o-container',
          fluid && 'ds-o-container--fluid',
          size === 'sm' && 'ds-o-container__sm',
          size === 'md' && 'ds-o-container__md',
          size === 'lg' && 'ds-o-container__lg',
          size === 'xl' && 'ds-o-container__xl',
          size === '2xl' && 'ds-o-container__2xl',
          margin && getMarginClasses(margin),
          padding && getPaddingClasses(padding),
          overflow && 'ds-max-w-full',
          overflow && typeof overflow === 'string'
            ? overflowXAuto[overflow]
            : '',
          overflow && typeof overflow === 'boolean' && 'ds-overflow-x-auto',
          className
        )}
        ref={ref}
        data-testid={testid}
        data-pkgid={`${packageDetails.get()}-container`}
        {...rest}
      >
        <ContainerContext.Provider value={{ overflow }}>
          {children}
        </ContainerContext.Provider>
      </Component>
    )
  }
)
Container.displayName = 'Container'
